<template>
  <div class="v-multiselect-wrapper">
    <div class="v-multiselect">
      <v-dropdown @toggle="isOpen = $event">
        <template slot-scope="props">
          <div
            class="v-multiselect__field"
            :class="{ 'v-multiselect__field--selected': value && value.length }"
          >
            <input
              v-focus
              v-if="props.toggled"
              v-model="search"
              type="text"
              class="v-multiselect__field-input"
              :placeholder="searchPlaceholder || 'Пошук'"
              @input="onSearchInput"
              @click.stop
            />
            <div
              v-else
              class="v-multiselect__placeholder"
              :class="{ 'v-multiselect__placeholder--hidden': selectedLabel === label }"
            >
              {{ selectedLabel | translate | srtstr }}
            </div>
            <label
              class="v-multiselect__label"
              :class="{ 'v-multiselect__label--active': selectedLabel !== label || labelActive || isOpen  }"
            >
              {{ label }}
            </label>
            <div class="v-multiselect__bar"></div>
            <div class="v-multiselect__chevron">
              <v-chevron :value="props.toggled" />
            </div>
          </div>
        </template>
        <template slot="content">
          <v-card elevated overflow-hidden>
            <div class="v-multiselect__actions">
              <div class="v-multiselect__actions-content">
                <div class="v-multiselect__buttons">
                  <button
                    @click="selectAll"
                    class="v-multiselect__action-btn v-multiselect__action-btn--select"
                    :disabled="loading || options.length === 0 || allVisibleSelected"
                  >
                    Всі видимі
                  </button>
                  <button
                    @click="unselectAll"
                    class="v-multiselect__action-btn v-multiselect__action-btn--unselect"
                    :disabled="loading || !hasAnySelected"
                  >
                    Скасувати видимі
                  </button>
                </div>
                <div class="v-multiselect__counter">
                  Вибрано: {{ value.length }}
                </div>
              </div>
            </div>
            <div
              class="v-multiselect__scroll-container"
              @scroll="onManualScroll"
            >
              <ul class="v-multiselect__options">
                <template v-if="sortedOptions.length">
                  <v-multiselect-item
                    v-for="item in sortedOptions"
                    :key="item.id"
                    :item="item"
                    :item-name="itemName"
                    :item-subname="itemSubname"
                    :item-value="itemValue"
                    :selected="value"
                    :comparator="comparator"
                    @select="onSelect"
                    @unselect="onUnselect"
                  />
                </template>
                <li v-else class="v-multiselect__options-info">
                  {{ loading ? 'Завантаження...' : 'Немає елементів' }}
                </li>
                <li v-if="sortedOptions.length > 0" class="v-multiselect__options-info v-multiselect__options-info--loaded">
                  Завантажено: {{ options.length }} елементів
                </li>
              </ul>
            </div>
          </v-card>
        </template>
      </v-dropdown>
    </div>
    <div class="v-multiselect__hint-wrapper">
      <div class="v-multiselect__hint" v-if="hint">{{ hint }}</div>
    </div>
  </div>
</template>

<script>
import VCard from '../v-card/VCard'
import VChevron from '../v-chevron/VChevron'
import VDropdown from '../v-dropdown/VDropdown'
import VScrollable from '../v-scrollable/VScrollable'
import VMultiselectItem from './VMultiselectItem'

export default {
  name: 'VMultiselectWithApiSearch',

  components: {
    VCard,
    VChevron,
    VDropdown,
    VScrollable,
    VMultiselectItem
  },

  props: {
    label: String,
    value: Array,
    options: Array,
    comparator: Function,
    hint: {
      type: String,
      default: null
    },
    labelActive: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: null
    },
    itemName: {
      type: String,
      default: 'name'
    },
    itemSubname: {
      type: String,
      default: null
    },
    itemValue: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    searchDebounce: {
      type: Number,
      default: 300
    }
  },

  data () {
    return {
      search: null,
      searchTimeout: null,
      isOpen: false
    }
  },

  computed: {
    selectedLabel () {
      return this.value.length
        ? this.value
          .map(item => this.$t(this.getItemNameByValue(item)))
          .join(', ')
        : this.label
    },

    visibleSelectedCount () {
      return this.options.filter(option => {
        return this.value.some(selected => {
          if (this.comparator) {
            return this.comparator(selected, option)
          }
          return selected.id === option.id
        })
      }).length
    },

    allVisibleSelected () {
      if (this.options.length === 0) return false
      return this.options.every(option => {
        return this.value.some(selected => {
          if (this.comparator) {
            return this.comparator(selected, option)
          }
          return selected.id === option.id
        })
      })
    },

    hasVisibleSelected () {
      return this.visibleSelectedCount > 0
    },

    hasAnySelected () {
      return this.value.length > 0
    },

    sortedOptions () {
      if (this.search) {
        return this.getSortedOptionsWithSearch()
      }
      return this.getSortedOptionsWithoutSearch()
    }
  },

  methods: {
    getSortedOptionsWithSearch () {
      const selected = []
      const unselected = []

      this.value.forEach(selectedItem => {
        const foundInOptions = this.options.find(option => {
          if (this.comparator) {
            return this.comparator(selectedItem, option)
          }
          return selectedItem.id === option.id
        })

        if (foundInOptions) {
          selected.push(foundInOptions)
        } else {
          selected.push(selectedItem)
        }
      })

      this.options.forEach(option => {
        const isSelected = this.value.some(selectedItem => {
          if (this.comparator) {
            return this.comparator(selectedItem, option)
          }
          return selectedItem.id === option.id
        })

        if (!isSelected) {
          unselected.push(option)
        }
      })

      return [...unselected, ...selected]
    },

    getSortedOptionsWithoutSearch () {
      const result = []

      this.options.forEach(option => {
        result.push(option)
      })

      this.value.forEach(selectedItem => {
        const foundInOptions = this.options.find(option => {
          if (this.comparator) {
            return this.comparator(selectedItem, option)
          }
          return selectedItem.id === option.id
        })

        if (!foundInOptions) {
          result.push(selectedItem)
        }
      })

      return result
    },

    onSearchInput () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.$emit('search', this.search)
      }, this.searchDebounce)
    },

    onSelect (item) {
      this.$emit('input', [...this.value, item])
    },

    onUnselect (item) {
      const id = item.id
      let value = [...this.value]
      value = value.filter(item => item.id !== id)
      this.$emit('input', value)
    },

    selectAll () {
      const newSelections = this.options.filter(option => {
        return !this.value.some(selected => {
          if (this.comparator) {
            return this.comparator(selected, option)
          }
          return selected.id === option.id
        })
      })

      this.$emit('input', [...this.value, ...newSelections])
    },

    unselectAll () {
      const displayedItemIds = this.sortedOptions.map(item => item.id)
      const filteredValue = this.value.filter(selected => {
        return !displayedItemIds.includes(selected.id)
      })

      this.$emit('input', filteredValue)
    },

    onScrollComplete () {
      this.$emit('scroll-complete')
    },

    onScroll (event) {
      const element = event.target
      const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50

      if (isAtBottom && !this.loading) {
        this.$emit('scroll-complete')
      }
    },

    onManualScroll (event) {
      const element = event.target
      const scrollTop = element.scrollTop
      const scrollHeight = element.scrollHeight
      const clientHeight = element.clientHeight

      if (scrollTop + clientHeight >= scrollHeight - 10) {
        if (!this.loading) {
          this.$emit('scroll-complete')
        }
      }
    },

    getItemNameByValue (item) {
      return !this.itemValue
        ? item[this.itemName]
        : this.options.find(el => el[this.itemValue] === item)[this.itemName]
    }
  },

  watch: {
    '$parent.toggled' (newVal) {
      if (!newVal) {
        this.search = null
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/sass/abstracts/_variables.scss';

.v-multiselect-wrapper {
  padding-top: 2.5rem;
}

.v-multiselect {
  position: relative;

  &__hint-wrapper {
    min-height: 1.5rem;
  }

  &__hint {
    color: $color-red;
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__actions {
    background-color: #f5f5f5;
    padding: 8px;
    border-bottom: 1px solid #e0e0e0;
  }

  &__actions-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  &__buttons {
    display: flex;
    gap: 6px;
  }

  &__action-btn {
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 11px;
    white-space: nowrap;
    transition: opacity 0.2s ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      opacity: 0.8;
    }

    &--select {
      background: #1da643;
      color: white;
    }

    &--unselect {
      background: #757575;
      color: white;
    }

    &--clear {
      background: #d32f2f;
      color: white;
    }
  }

  &__counter {
    font-size: 11px;
    color: #666;
    white-space: nowrap;
  }

  &__field {
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 4rem;
    padding: 1rem;
    border-radius: 1rem;
    background: $color-hover;

    // &--selected {
    //   background: #ddf2e3;
    // }

    &-input {
      width: 100%;
      border: none;
      font-size: 1.4rem;
      background: transparent;

      &:focus {
        outline: none;
      }

      &:focus ~ .v-multiselect__bar:before,
      &:focus ~ .v-multiselect__bar:after {
        width: 50%;
      }
    }
  }

  &__placeholder {
    user-select: none;
    pointer-events: none;
    font-size: 1.5rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: 24rem;

    &--hidden {
      opacity: 0;
    }
  }

  &__label {
    color: $color-black;
    font-size: 1.5rem;
    position: absolute;
    pointer-events: none;
    line-height: 1.3rem;
    top: 1.2rem;
    left: 1rem;
    transition: 0.2s ease all;

    &--active {
      color: $color-gray;
      font-size: 1.3rem;
      top: -2.2rem;
      left: 0;
    }
  }

  &__chevron {
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
  }

  &__scroll-container {
    max-height: 300px;
    overflow-y: auto;
  }

  &__options {
    padding: 0;
    margin: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    background-color: white;
    padding-top: 4px;

    &-info,
    &-item {
      font-size: 1.7rem;
      padding: 1.3rem 1.6rem;
    }

    &-info {
      text-align: center;
      font-size: 14px;
      color: #757575;
    }

    &-item {
      position: relative;
      cursor: pointer;
      user-select: none;
      padding-left: 5rem;
      display: flex;
      flex-direction: column;
      word-break: break-all;
      transition: 0.2s all ease;
      width: calc(100% - 8px);
      margin: 0 auto;
      margin-bottom: 4px;
      

      &--subtitle {
        color: $color-gray;
        font-size: 1.2rem;
        padding-top: 0.5rem;
      }

      // &:not(:last-child) {
      //   border-bottom: 1px solid $color-gray-light-3;
      // }

      &:hover {
        background-color: #F4F4F4;
      }

      &::before {
        content: '';
        position: absolute;
        left: 1.3rem;
        top: 50%;
        transform: translateY(-50%);
        height: 2rem;
        width: 2rem;
        border-radius: 4px;
        border: 0.2rem solid #CCCCCC;
        background-color: #fff;
      }
      &--selected {
        background-color: #DDF2E3;
      }
      &--selected::before {
        border: 0.2rem solid #59CC56;
      }
      &--selected::after {
        content: '';
        position: absolute;
        left: 1.6rem;
        top: 50%;
        transform: translateY(-50%);
        height: 1rem;
        width: 1.4rem;
        background-image: url('../../../assets/img/check.svg');
      }
    }
  }
}
</style>